<template>
  <div class="story">
    <sheet>
      <loading v-if="isLoading"></loading>
      <template v-else>
        <h3 class="story-title">{{ lessonData.title }}</h3>
        <div class="time-type">
          <p class="time-type__time">{{ lessonData.formatPublished }}</p>
          <p class="time-type__type">{{ identificationMapping[String(lessonData.user_type)] }}</p>
          <p
            class="time-type__status"
            :class="{
              'time-type__status--notOpenYet': applyStatusType(lessonData) === 'notOpenYet',
              'time-type__status--registered': applyStatusType(lessonData) === 'registered',
              'time-type__status--expired': applyStatusType(lessonData) === 'expired',
              'time-type__status--full': applyStatusType(lessonData) === 'full',
              'time-type__status--available': applyStatusType(lessonData) === 'available',
            }"
          >
            {{ statusMapping[applyStatusType(lessonData)] }}
          </p>
        </div>
        <divide dashed mt="20" mb="55" />
        <div class="article-content" v-html="lessonData.content"></div>

        <lesson-info :lessonData="lessonData">
          <div class="lesson__box-wrap">
            <pr-button
              @click="handleApply(lessonData.user_type)"
              :disabled="applyStatusType(lessonData) !== 'available'"
              >{{ statusMapping[applyStatusType(lessonData)] }}</pr-button
            >
          </div>
        </lesson-info>

        <divide dashed mt="110" mb="30" />
        <more-lesson-activity title="更多推薦活動" />
      </template>
    </sheet>

    <pr-dialog v-model:visible="dialogVisible.pleaseLogin" appendToBody>
      <!-- FIXED: 應從內部去關閉 -->
      <please-login @closeDialog="closeDialog" />
    </pr-dialog>
  </div>
</template>

<script>
import {
  defineComponent, reactive, watch, ref,
} from 'vue';
import dayjs from 'dayjs';
import { useRoute, useRouter } from 'vue-router';
import Divide from '@/components/Global/Divide.vue';
import MoreLessonActivity from '@/components/News/MoreLessonActivity.vue';
import PrButton from '@/components/Global/PrButton.vue';
import PleaseLogin from '@/components/Dialog/PleaseLogin.vue';
import PrDialog from '@/components/Global/Dialog.vue';
import { getIndexEventsAPI } from '@/api/events';
import { identificationMapping } from '@/config/identificaitionConfig';
import { useSiteStore } from '@/store/site';
import LessonInfo from '@/components/News/LessonInfo.vue';
import useRequest from '@/hooks/useRequest';
import { statusMapping, applyStatusType } from '@/config/applyStatus';
import { useExtraBreadcrumbStore } from '@/store/extraBreadcrumb';

export default defineComponent({
  name: 'ActivityLesson',
  components: {
    Divide,
    MoreLessonActivity,
    PrButton,
    PleaseLogin,
    PrDialog,
    LessonInfo,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const lessonData = ref({});
    const siteStore = useSiteStore();
    const extraBreadcrumbStore = useExtraBreadcrumbStore();
    // const applyStatus = ref('');
    const { request, response, isLoading } = useRequest(getIndexEventsAPI);
    request({
      uuid: route.query.id,
    });

    const dialogVisible = reactive({
      pleaseLogin: false,
    });

    const openDialog = (type) => {
      dialogVisible[type] = true;
    };

    const closeDialog = (type) => {
      dialogVisible[type] = false;
    };

    const handleApply = (userType) => {
      if (siteStore.isLogin) {
        console.log('isLogin');
        console.log('userType', userType);

        router.push({
          // name: 'activity-lesson-apply',
          name: userType === 1 ? 'activity-lesson-parent-apply' : 'activity-lesson-team-apply',
          query: {
            id: route.query.id,
          },
        });

        return;
      }

      openDialog('pleaseLogin');
    };

    const getIndexEvents = async (res) => {
      try {
        const formatArticle = {
          ...res.value.result,
          formatPublished: dayjs(res.value.result.published).format('YYYY-MM-DD'),
          formatEventStartTime: dayjs(res.value.result.event_start_time).format('YYYY-MM-DD HH:mm'),
          formatEventEndTime: dayjs(res.value.result.event_end_time).format('YYYY-MM-DD HH:mm'),
          formatApplyStartTime: dayjs(res.value.result.apply_start_date).format('YYYY-MM-DD HH:mm'),
          formatApplyEndTime: dayjs(res.value.result.apply_end_date).format('YYYY-MM-DD HH:mm'),
        };
        lessonData.value = formatArticle;
        extraBreadcrumbStore.setExtraBreadcrumb(formatArticle.title);
      } catch (e) {
        console.log('e', e);
      }
    };

    watch(response, () => {
      getIndexEvents(response);
    });

    return {
      dialogVisible,
      openDialog,
      closeDialog,
      handleApply,
      lessonData,
      identificationMapping,
      isLoading,
      applyStatusType,
      statusMapping,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@/styles/share/pureText.scss';
@import '@/styles/share/article.scss';

.story-title {
  @include font-style($c-main, 35, 500, 3.5px, 51px);
}

.time-type {
  @include flex();

  &__time {
    @include font-style($c-main, 16, 400, 1.6px, 28px);
  }

  &__type {
    position: relative;
    margin-left: 23px;
    @include font-style($c-assist-pink, 16, 400, 1.6px, 28px);

    &::before {
      content: '';
      width: 1px;
      height: 20px;
      @include position(tl, 50%, -12px);
      transform: translateY(-50%);
      display: inline-block;
      background-color: $c-main;
    }
  }

  &__status {
    position: relative;
    margin-left: 23px;
    @include font-style($c-assist-green, 16, 400, 1.6px, 28px);

    &--notOpenYet {
      color: rgba($c-main-yellow, 0.8);
    }

    &--expired {
      color: rgba($c-grey, 0.5);
    }

    &--full {
      color: rgba($c-red, 0.5);
    }

    &--available {
      color: $c-assist-green;
    }

    &--registered {
      color: rgba($c-main-yellow, 0.8);
    }

    &::before {
      content: '';
      width: 1px;
      height: 20px;
      @include position(tl, 50%, -12px);
      transform: translateY(-50%);
      display: inline-block;
      background-color: $c-main;
    }
  }
}

.img-box {
  &__img {
    width: 100%;
    max-width: 100%;
  }
}

.lesson {
  &__box-wrap {
    margin-top: 20px;
  }
}

@media screen and (min-width: $tablet) {
  .img-box {
    &__img {
      max-width: 763px;
    }
  }
}
</style>
